// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LocalizationProvider } from './contexts/LocalizationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AlertProvider } from './contexts/AlertContext';
import { ExportProvider } from './contexts/ExportContext';
import Layout from './components/common/Layout/Layout';
import AlertContainer from './components/common/AlertContainer/AlertContainer';
import { SubscriptionGuard } from './components/common/Subscription';
import ProtectedRoute from './routes/ProtectedRoute';
import PermissionRoute from './routes/PermissionRoute';
import UserManagementRoutes from './routes/UserManagementRoutes';
import ShopRoutes from './routes/ShopRoutes';
import ProductRoutes from './routes/ProductRoutes';
import EcomOrderRoutes from './routes/EcomOrderRoutes';
import OrderAssignmentRoutes from './routes/OrderAssignmentRoutes';
import CourierManagementRoutes from './routes/CourierManagementRoutes';
import PackingBatchRoutes from './routes/PackingBatchRoutes';
import PaymentVerificationRoutes from './routes/PaymentVerificationRoutes';
import StorePickupRoutes from './routes/StorePickupRoutes';
import CourierInvoiceRoutes from './routes/CourierInvoiceRoutes';
import ReturnBatchRoutes from './routes/ReturnBatchRoutes';
import InventoryRoutes from './routes/InventoryRoutes';
import SupplierRoutes from './routes/SupplierRoutes';
import ProcurementRoutes from './routes/ProcurementRoutes';
import ReportRoutes from './routes/ReportRoutes';
import SubscriptionRoutes from './routes/SubscriptionRoutes';
import HRRoutes from './routes/HRRoutes';
import SmsConfigRoutes from './routes/SmsConfigRoutes';
import DropshippingRoutes from './routes/DropshippingRoutes';
import ExpenseRoutes from './routes/ExpenseRoutes';
import LeadAutomationRoutes from './routes/LeadAutomationRoutes';
import MessageCenterRoutes from './routes/MessageCenterRoutes';
import CallCenterRoutes from './routes/CallCenterRoutes';
import JoinProviderPage from './pages/dropshipping/JoinProviderPage';
import PublicInvoicePage from './pages/public/PublicInvoicePage';
import OfflineSalesRoutes from './routes/OfflineSalesRoutes';
import OfflineSalesTerminalLayout from './components/common/OfflineSalesTerminalLayout/OfflineSalesTerminalLayout';
import OfflineSalesTerminal from './pages/offlineSales/terminal/OfflineSalesTerminal';
import CustomerDisplayPage from './pages/offlineSales/customerDisplay/CustomerDisplayPage';

// Auth pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';

// Dashboard
import Dashboard from './pages/dashboard/Dashboard';

// Profile
import EditProfilePage from './pages/profile/EditProfilePage';

// Initial Setup Wizard
import InitialSetupWizard from './components/setup/InitialSetupWizard';

import './App.css';

/**
 * Inner component to access auth context for ThemeProvider
 * ThemeProvider needs user data from AuthProvider to initialize theme
 */
function AppContent() {
  const { user, refreshUser } = useAuth();

  // Check if setup is required
  const isSetupRequired = user && !user.business?.setupCompleted;

  // If setup is required, show only the setup wizard
  if (isSetupRequired) {
    return (
      <ThemeProvider user={user}>
        <AlertProvider>
          <div className="App">
            <InitialSetupWizard onComplete={refreshUser} />
            <AlertContainer />
          </div>
        </AlertProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider user={user}>
      <AlertProvider>
        <ExportProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="login" element={<Login />} />
              <Route path="signup" element={<Signup />} />
              <Route path="join/:hash" element={<JoinProviderPage />} />
              <Route path="invoice/:token" element={<PublicInvoicePage />} />

              {/* Customer-facing second screen for the terminal (data comes from the terminal window) */}
              <Route path="/offline-sales/customer-display/:shopId" element={
                <ProtectedRoute>
                  <CustomerDisplayPage />
                </ProtectedRoute>
              } />

              {/* Offline Sales Terminal — full-screen, no sidebar */}
              <Route path="/offline-sales/terminal/:shopId" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <OfflineSalesTerminalLayout />
                  </SubscriptionGuard>
                </ProtectedRoute>
              }>
                <Route index element={
                  <PermissionRoute requiredPermission="create_offline_sales_order">
                    <OfflineSalesTerminal />
                  </PermissionRoute>
                } />
              </Route>

              <Route path="/" element={<Layout />}>
                {/* Dashboard - No subscription guard */}
                <Route 
                  path="dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Subscription Routes - No subscription guard (users must access this even when expired) */}
                <Route
                  path="subscription/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionRoutes />
                    </ProtectedRoute>
                  }
                />

                {/* Profile - No subscription guard (users must always access their profile) */}
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <EditProfilePage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Business Operation Routes - WITH subscription guard */}
                <Route 
                  path="users/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <UserManagementRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="shops/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ShopRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="products/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ProductRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="orders/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <EcomOrderRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="order-assignments/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <OrderAssignmentRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="couriers/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <CourierManagementRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="packing-batches/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <PackingBatchRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="payments/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <PaymentVerificationRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="store-pickup/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <StorePickupRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="courier-invoices/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <CourierInvoiceRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="return-batches/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ReturnBatchRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="inventory/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <InventoryRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="suppliers/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <SupplierRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="procurement/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ProcurementRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="reports/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ReportRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="hr/*" 
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <HRRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="sms-config/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <SmsConfigRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="dropshipping/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <DropshippingRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="offline-sales/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <OfflineSalesRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="expenses/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <ExpenseRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="lead-automation/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <LeadAutomationRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="message-center/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <MessageCenterRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="call-center/*"
                  element={
                    <ProtectedRoute>
                      <SubscriptionGuard>
                        <CallCenterRoutes />
                      </SubscriptionGuard>
                    </ProtectedRoute>
                  }
                />

                {/* Default redirect */}
                <Route index element={<Navigate to="/dashboard" replace />} />
                
                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
            
            {/* Global Alert Container */}
            <AlertContainer />
          </div>
        </Router>
        </ExportProvider>
      </AlertProvider>
    </ThemeProvider>
  );
}

/**
 * Main App component with proper provider hierarchy
 * 
 * Provider Order (outside to inside):
 * 1. LocalizationProvider - Handles language/translations
 * 2. AuthProvider - Handles authentication/user data
 * 3. ThemeProvider - Handles themes (needs user data from AuthProvider)
 * 4. AlertProvider - Handles global alerts
 * 5. Router - Handles routing
 */
function App() {
  return (
    <LocalizationProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LocalizationProvider>
  );
}

export default App;